import React, { useState } from 'react';
import { useUser } from '@clerk/clerk-react';
import { Mail, Plus, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';

export const SettingsEmailCard: React.FC = () => {
  const { user } = useUser();
  const [newEmail, setNewEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [pendingVerification, setPendingVerification] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const emailAddresses = user?.emailAddresses || [];

  const handleAddEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !newEmail.trim()) return;

    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const emailResource = await user.createEmailAddress({ email: newEmail.trim() });
      await emailResource.prepareVerification({ strategy: 'email_code' });
      setPendingVerification(emailResource);
      setIsAdding(false);
      setSuccess('Verification code sent to your new email.');
    } catch (err: any) {
      setError(err?.errors?.[0]?.message || err?.message || 'Failed to add email address.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pendingVerification || !verificationCode.trim()) return;

    setIsLoading(true);
    setError(null);

    try {
      await pendingVerification.attemptVerification({ code: verificationCode.trim() });
      await user?.reload();
      setPendingVerification(null);
      setVerificationCode('');
      setNewEmail('');
      setSuccess('Email successfully verified and linked.');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err?.errors?.[0]?.message || err?.message || 'Invalid verification code.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-[#0E1015] border border-zinc-800 rounded-2xl p-6">
      <div className="flex items-center justify-between pb-4 border-b border-zinc-800 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-indigo-600/10 border border-indigo-500/20 flex items-center justify-center text-indigo-400">
            <Mail className="w-4 h-4" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-white">Email Addresses</h2>
            <p className="text-xs text-zinc-400">Manage account emails for notifications and security</p>
          </div>
        </div>

        {!isAdding && !pendingVerification && (
          <button
            type="button"
            onClick={() => setIsAdding(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded-xl text-xs font-medium transition cursor-pointer"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>Add Email</span>
          </button>
        )}
      </div>

      {success && (
        <div className="mb-4 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4" />
          <span>{success}</span>
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-400 text-xs flex items-center gap-2">
          <AlertCircle className="w-4 h-4" />
          <span>{error}</span>
        </div>
      )}

      <div className="space-y-3 mb-6">
        {emailAddresses.map((emailObj) => {
          const isPrimary = emailObj.id === user?.primaryEmailAddressId;
          const isVerified = emailObj.verification?.status === 'verified';

          return (
            <div
              key={emailObj.id}
              className="flex items-center justify-between p-3.5 rounded-xl bg-zinc-950 border border-zinc-850 text-xs"
            >
              <div className="flex items-center gap-3">
                <span className="font-mono text-zinc-200">{emailObj.emailAddress}</span>
                {isPrimary && (
                  <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-indigo-500/15 text-indigo-400 border border-indigo-500/30">
                    Primary
                  </span>
                )}
                {isVerified ? (
                  <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
                    Verified
                  </span>
                ) : (
                  <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-amber-500/15 text-amber-400 border border-amber-500/30">
                    Unverified
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {isAdding && (
        <form onSubmit={handleAddEmail} className="p-4 rounded-xl bg-zinc-900/60 border border-zinc-800 space-y-3">
          <h3 className="text-xs font-semibold text-white">Add an alternate email</h3>
          <div className="flex gap-2">
            <input
              type="email"
              required
              placeholder="e.g. partner-work@conversora.io"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              className="flex-1 bg-zinc-950 border border-zinc-800 rounded-xl px-3.5 py-2 text-xs text-white focus:outline-none focus:border-indigo-500"
            />
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white rounded-xl text-xs font-semibold transition cursor-pointer flex items-center gap-1.5"
            >
              {isLoading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              <span>Send Code</span>
            </button>
            <button
              type="button"
              onClick={() => setIsAdding(false)}
              className="px-3 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-xl text-xs font-medium transition cursor-pointer"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {pendingVerification && (
        <form onSubmit={handleVerifyCode} className="p-4 rounded-xl bg-zinc-900/60 border border-indigo-500/40 space-y-3">
          <h3 className="text-xs font-semibold text-white">Enter the verification code</h3>
          <p className="text-[11px] text-zinc-400">
            We sent a verification code to <span className="text-white font-medium">{pendingVerification.emailAddress}</span>.
          </p>
          <div className="flex gap-2">
            <input
              type="text"
              required
              placeholder="Enter 6-digit code"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              className="flex-1 bg-zinc-950 border border-zinc-800 rounded-xl px-3.5 py-2 text-xs text-white tracking-widest text-center focus:outline-none focus:border-indigo-500"
            />
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white rounded-xl text-xs font-semibold transition cursor-pointer flex items-center gap-1.5"
            >
              {isLoading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              <span>Verify & Link</span>
            </button>
            <button
              type="button"
              onClick={() => setPendingVerification(null)}
              className="px-3 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-xl text-xs font-medium transition cursor-pointer"
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
};
